import React, { ForwardedRef, forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import GUI from "../GUI";

export type TTransitionTypes = "none"|"fade"|"slideUp"|"slideDown"
export type TTransitionProps = React.ComponentPropsWithoutRef<"div"> & {
    transition?:TTransitionTypes
    children?:React.ReactNode
    beforeEntering?:Function
    afterEntering?:Function
    beforeLeaving?:Function
    afterLeaving?:Function
    onCancel?:Function
}

export interface ITransition {
    target:HTMLDivElement|null
}
function Transition(props:TTransitionProps, ref:ForwardedRef<ITransition>){
    const {className,children:_children,transition,beforeEntering,afterEntering,beforeLeaving,afterLeaving,onCancel,...divProps} = props
    const [children,setChildren] = useState<React.ReactNode|null>(props.children||null)
    const refSelf = useRef<HTMLDivElement | null>(null)
    const transitionId = useRef<number>(0)
    const signal = useRef<AbortController|null>(null)

    const refCB = useCallback((div:HTMLDivElement|null) => {
        refSelf.current = div
        div && enter()
    }, [])

    useImperativeHandle(ref, () => ({
        get target() { return refSelf.current }
    }), [refSelf])

    const clear = () => refSelf.current?.classList.remove("before-enter", "before-leave", "after-enter", "after-leave")
    const animate = async (type:"enter"|"leave") => {
        if (!refSelf.current) return

        const id = ++transitionId.current
        const isCancelled = () => id!=transitionId.current || !refSelf.current
        const isEntering = type=="enter"

        clear()
            
        refSelf.current.classList.add("transition-active", `before-${type}`)
        await GUI.tick()
        await GUI.tick()
        if (isCancelled()) return onCancel?.() 

        if (isEntering) beforeEntering?.()
        else beforeLeaving?.()
        
        refSelf.current.classList.remove(`before-${type}`)
        refSelf.current.classList.add(`after-${type}`)
        signal.current = new AbortController()
        try {
            await GUI.transitionEnd(refSelf.current, signal.current.signal)
            if (isCancelled()) return onCancel?.()
        }catch(e){
            // aborted
            onCancel?.()
            return clear()
        }

        if (isEntering){
            refSelf.current.classList.remove("after-enter", "transition-active")
            afterEntering?.()
        }else{
            setChildren(null)
            afterLeaving?.()
        }        
    }
    const enter = () => {
        if (transition && transition!="none") return animate("enter")
        beforeEntering?.()
        afterEntering?.()
    }
    const leave = () => {
        if (transition && transition!="none") return animate("leave")
        beforeLeaving?.()
        afterLeaving?.()
    }

    useEffect(() => {
        if (props.children) {
            setChildren(props.children)
            if (signal.current) {
                signal.current.abort()
                signal.current = null
            }
        }
        else if (children) {
            if (signal.current) {
                signal.current.abort()
                signal.current = null
            }
            leave()
        }
    }, [props.children])

    return children ? <div className={GUI.joinClass(props.className, transition)} ref={refCB} {...divProps}>{children}</div> : <></>
}

export default forwardRef(Transition)